/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Space, Spin, Typography } from 'antd';
import { ExternalLinkIcon } from '../Icons/ExternalLinkIcon';

// Misc imports
import { NAV_BOT } from '../../constants/navigation';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { useFetchNlpPipelineQuery } from '../../store/nlpPipelines/nlpPipelinesApi';

/**
 * Given a pipeline ID, display it as a text that links to the corresponding pipeline
 * @param {String} pipelineId The ID of the pipeline
 * @param {Object} pipeline The pipeline object, if available. This will skip the API call
 * @param {Boolean} showIcon Whether to show the external link icon
 * @returns {JSX.Element} The pipeline link
 */
const NlpPipelineLink = ({
  pipelineId,
  pipeline,
  showIcon = true,
  loader = <Spin size="small" />
}) => {
  const { orgId, tenantId } = useParams();
  const { t } = useTranslation();

  const { data: nlpPipeline, isFetching: isFetchingNlpPipeline } = useFetchNlpPipelineQuery({ orgId, tenantId, nlpPipelineId: pipelineId }, { skip: !pipelineId || pipeline });
  const pipelineToDisplay = pipeline || nlpPipeline;

  if (isFetchingNlpPipeline) {
    return loader;
  } else if (pipelineToDisplay) {
    return (
      <Fragment>
        <Typography.Link
          className="p-0 w-auto word-break-break-all pointer-events-all"
          onClick={() => {
            window.open(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?view=artificial-intelligence&tab=nlp-systems&pipelineId=${pipelineToDisplay.id}`);
          }}
        >
          <Space>
            {pipelineToDisplay.name}
            {showIcon ? <ExternalLinkIcon /> : null}
          </Space>
        </Typography.Link>
      </Fragment>
    );
  } else if (!tenantId && pipelineId) {
    return (
      <Typography.Link
        className="p-0 w-auto word-break-break-all"
      >
        <Space>
          {pipelineToDisplay || t('Unknown pipeline')}
          {showIcon ? <ExternalLinkIcon /> : null}
        </Space>
      </Typography.Link>
    );
  } else {
    return t('Unknown pipeline');
  }
};

NlpPipelineLink.propTypes = {
  pipelineId: PropTypes.string,
  pipeline: PropTypes.object,
  showIcon: PropTypes.bool,
  loader: PropTypes.node
};

export default NlpPipelineLink;
